import { useQueryClient } from '@tanstack/react-query';
import ms from 'ms';
import { useEffect, useState } from 'react';

import { type UUID } from '~/types/common';

import { fetchCostCenter, queryKeysCostCenter } from '~/data/costCenter';
import { fetchSite, queryKeysSite } from '~/data/site';
import { fetchUser, queryKeysUser, type User } from '~/data/user';

import { CostCenterObject } from '~/models/masterdata/CostCenter';
import { SiteObject } from '~/models/masterdata/Site';
import { UserObject } from '~/models/masterdata/User';

import { uniqueBy } from '@vestigas/utils';
import { handlePromise, promiseAllThrottled } from '~/utils/promise';

import { type DeliveryNoteCreatedValues } from '../../types';

type InitPermittedUsersProps = {
  deliveryNotes: DeliveryNoteCreatedValues[];
};

export const useInitPermittedUsers = ({
  deliveryNotes,
}: InitPermittedUsersProps) => {
  const queryClient = useQueryClient();
  const [allPermittedUserIds, setAllPermittedUserIds] = useState<UUID[]>([]);
  const [allPermittedUsers, setAllPermittedUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const initPermittedUsers = async (
    permittedUsers: Array<Record<string, any>>,
    permittedToSites: Array<Record<string, any>>,
    permittedCostCenters: Array<Record<string, any>>,
  ) => {
    const entityPromises = [
      ...permittedToSites.map(({ id }) => ({
        async getEntityById(siteId: UUID) {
          const response = await queryClient.fetchQuery({
            meta: {
              suppressError: true,
            },
            queryFn: async () => fetchSite(siteId),
            queryKey: queryKeysSite.get(siteId),
            staleTime: ms('60s'),
          });

          return SiteObject.create(response);
        },
        id,
      })),
      ...permittedCostCenters.map(({ id }) => ({
        async getEntityById(costCenterId: UUID) {
          const response = await queryClient.fetchQuery({
            meta: {
              suppressError: true,
            },
            queryFn: async () => fetchCostCenter(costCenterId),
            queryKey: queryKeysCostCenter.get(costCenterId),
            staleTime: ms('60s'),
          });

          return CostCenterObject.create(response);
        },
        id,
      })),
    ];

    const fetchUserCallback = async (userId: UUID) => {
      return queryClient.fetchQuery({
        meta: {
          suppressError: true,
        },
        queryFn: async () => fetchUser(userId),
        queryKey: queryKeysUser.get(userId),
        staleTime: ms('60s'),
      });
    };

    const userPromises = entityPromises.map(async ({ id, getEntityById }) =>
      handlePromise(
        UserObject.getPermittedUsersOfEntity(
          id,
          getEntityById,
          fetchUserCallback,
        ),
      ),
    );

    const results = await promiseAllThrottled(userPromises);

    const allPermittedUsers = results.reduce<any[]>(
      (users, [newUsers, error]) => {
        if (error) {
          // The error is already logged in the api call. And we should not throw here, as it will break the flow.
          return users;
        }

        return [...users, ...newUsers];
      },
      [],
    );

    const newPermittedUsers = uniqueBy(
      [...permittedUsers, ...allPermittedUsers],
      ({ id }) => id,
    ).filter(({ isActive }) => isActive); // Remove inactive users

    return newPermittedUsers;
  };

  useEffect(() => {
    const fetchPermittedUsers = async () => {
      setIsLoading(true);

      const allIds: UUID[] = [];
      const allUsers: User[] = [];

      for (const deliveryNote of deliveryNotes) {
        const { permittedUsers, permittedToSites, permittedCostCenters } =
          deliveryNote;

        const newPermittedUsers = await initPermittedUsers(
          permittedUsers,
          permittedToSites,
          permittedCostCenters,
        );

        deliveryNote.initPermittedUsers(newPermittedUsers);

        allIds.push(...newPermittedUsers.map(({ id }) => id));
        allUsers.push(...newPermittedUsers);
      }

      setAllPermittedUserIds(uniqueBy(allIds, (id) => id));
      setAllPermittedUsers(allUsers);
      setIsLoading(false);
    };

    if (deliveryNotes.length > 0) {
      fetchPermittedUsers();
    }
  }, [deliveryNotes]);

  return {
    permittedUserIds: allPermittedUserIds,
    isLoading,
    permittedUsers: allPermittedUsers,
  };
};
